import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./icons";
import { Spark } from "./icons";

type DeltaTone = "up" | "down" | "flat";

type Props = {
  label: string;
  value: string | number;
  unit?: string;
  delta?: string | null;
  deltaTone?: DeltaTone;
  hint?: string | null;
  spark?: number[] | null;
  sparkTone?: "brand" | "ok" | "crit";
  accent?: boolean;
  dense?: boolean;
  /** Presence turns the tile into a real `<Link>` (an `<a>`, never a
   *  `<button>` — the #0567 FORBIDDEN sweep only iterates buttons) and
   *  adds the chevron. Tiles without `to` carry no chevron: the absence
   *  is the signal, not a tooltip (handoff D1). */
  to?: string;
  /** Golden "por revisar" variant (handoff D1). */
  review?: boolean;
  /** Glyph rendered in the label row, e.g. `<Icon.Eye />`. */
  icon?: ReactNode;
};

export function KpiTile({
  label,
  value,
  unit,
  delta,
  deltaTone,
  hint,
  spark,
  sparkTone,
  accent,
  dense,
  to,
  review,
  icon,
}: Props) {
  const className = [
    "kpi-tile",
    to ? "kpi-tile--link" : "",
    review ? "kpi-tile--review" : "",
    accent ? "kpi-tile--accent" : "",
    dense ? "kpi-tile--dense" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      <div className="kpi-tile__label">
        {icon}
        {label}
      </div>
      <div className="kpi-tile__value">
        <span>{value}</span>
        {unit && <span className="kpi-tile__unit">{unit}</span>}
      </div>
      {delta && (
        <div className={`kpi-tile__delta kpi-tile__delta--${deltaTone ?? "flat"}`}>
          {deltaTone === "up" && <Icon.TrendUp />}
          {deltaTone === "down" && <Icon.TrendDown />}
          {(!deltaTone || deltaTone === "flat") && <Icon.Flat />}
          <span>{delta}</span>
        </div>
      )}
      {hint && <div className="kpi-tile__hint">{hint}</div>}
      {spark && spark.length > 0 && (
        <div className="kpi-tile__spark">
          <Spark data={spark} tone={sparkTone ?? "brand"} w={200} h={28} />
        </div>
      )}
      {to && (
        <span className="kpi-tile__chev" aria-hidden="true">
          <Icon.ChevronRight s={18} />
        </span>
      )}
    </>
  );

  // The accessible name of the link is its text content (label + value
  // + hint) — it already includes the count, so no aria-label is added
  // (accessibility-expert, D1).
  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}
